import { traverse } from '@atlaskit/adf-utils/traverse/main';
import type { ADFEntity } from '@atlaskit/adf-utils/types';
import {
	NATIVE_EMBED_EXTENSION_KEY,
	NATIVE_EMBED_EXTENSION_TYPE,
} from '@atlaskit/editor-common/extensions/manifest-helpers';
import { sanitizeNode } from '@atlaskit/editor-json-transformer/sanitize/sanitize-node';
import type { JSONNode } from '@atlaskit/editor-json-transformer/types';

import { isRemixRestrictedNodeType } from '../remix/remix-config';

/**
 * Filters an ADF fragment (array of JSONNode) by recursively removing nodes
 * whose type is in the Remix deny list at every level of the tree.
 * Returns null when given null.
 *
 * Uses the adf-utils `traverse` helper which drops nodes when a visitor
 * returns `false` and automatically rebuilds content arrays.
 */
/**
 * Extracts frame titles from a multiBodiedExtension's parameters.
 *
 * Searches for any array property in `parameters` whose length matches the
 * frame count and whose items have a `title` field. This avoids hard-coding
 * macro-specific parameter names (e.g. `tabs`) so it scales to any MBE
 * variant that stores per-frame titles in its parameters.
 */
const extractFrameTitles = (
	node: JSONNode,
	frameCount: number,
): Array<string | undefined> | undefined => {
	const attrs = node.attrs as Record<string, unknown> | undefined;
	const parameters = attrs?.parameters as Record<string, unknown> | undefined;
	if (!parameters) {
		return undefined;
	}
	for (const value of Object.values(parameters)) {
		if (
			Array.isArray(value) &&
			value.length === frameCount &&
			value.some((item) => typeof item === 'object' && item !== null && 'title' in item)
		) {
			return value.map((item: { title?: string }) => item.title);
		}
	}
	return undefined;
};

/**
 * Unwraps a multiBodiedExtension node, extracting inner content from each
 * extensionFrame so the AI receives clean ADF (paragraphs, lists, etc.)
 * instead of extension wrapper types it cannot process.
 *
 * When frame titles are available in the node's parameters, a heading is
 * injected before each frame's content so the AI preserves the logical
 * grouping rather than treating everything as one flat list.
 */
const unwrapMultiBodiedExtension = (
	node: JSONNode,
	result: JSONNode[],
	isNodeUnsupported: IsNodeUnsupportedForRemix,
): void => {
	const frames = (node.content ?? []).filter(
		(frame): frame is JSONNode => frame?.type === 'extensionFrame' && Array.isArray(frame?.content),
	);
	const frameTitles = extractFrameTitles(node, frames.length);

	frames.forEach((frame, index) => {
		const title = frameTitles?.[index];
		if (title) {
			result.push({
				type: 'heading',
				attrs: { level: 2 },
				content: [{ type: 'text', text: title }],
			});
		}

		for (const child of frame.content!) {
			const filtered = traverse(child as ADFEntity, {
				any: (adfNode) => {
					if (isNodeUnsupported(adfNode)) {
						return false;
					}
				},
			});
			if (filtered !== false) {
				result.push(filtered as JSONNode);
			}
		}
	});
};

/**
 * Product-owned rule for which ADF nodes Remix cannot handle. Matches the shape of
 * `RemixProductConfig.isNodeUnsupportedForRemix` so a product config can be passed straight
 * through. Defaults to the shared deny list.
 */
type IsNodeUnsupportedForRemix = (node: ADFEntity) => boolean;

const defaultIsNodeUnsupportedForRemix: IsNodeUnsupportedForRemix = (node) =>
	isRemixRestrictedNodeType(node.type);

export const filterRemixFragmentNodes = (
	nodes: JSONNode[] | null,
	isNodeUnsupported: IsNodeUnsupportedForRemix = defaultIsNodeUnsupportedForRemix,
): JSONNode[] | null => {
	if (nodes === null) {
		return null;
	}

	const result: JSONNode[] = [];
	for (const node of nodes) {
		if (node.type === 'multiBodiedExtension' && Array.isArray(node.content)) {
			unwrapMultiBodiedExtension(node, result, isNodeUnsupported);
			continue;
		}

		const filtered = traverse(node as ADFEntity, {
			any: (adfNode) => {
				if (isNodeUnsupported(adfNode)) {
					return false;
				}
			},
		});
		if (filtered !== false) {
			result.push(filtered as JSONNode);
		}
	}
	return result;
};

/**
 * Converts editor selection JSON to canonical ADF before applying Remix's existing node filter.
 */
export const prepareRemixFragmentNodes = (
	nodes: JSONNode[] | null,
	isNodeUnsupported?: IsNodeUnsupportedForRemix,
): JSONNode[] | null => {
	if (nodes === null) {
		return null;
	}

	const sanitizedNodes = nodes.flatMap((node) => {
		// The sanitizer can remove an invalid root node at runtime even though its legacy return
		// type only exposes JSONNode.
		const sanitizedNode = sanitizeNode(node, { keepNestedTables: true }) as JSONNode | false;
		return sanitizedNode === false ? [] : [sanitizedNode];
	});

	return filterRemixFragmentNodes(sanitizedNodes, isNodeUnsupported);
};

const STRUCTURAL_NODE_TYPES = new Set([
	'blockquote',
	'bodiedExtension',
	'bodiedSyncBlock',
	'bulletList',
	'codeBlock',
	'decisionItem',
	'decisionList',
	'doc',
	'expand',
	'extensionFrame',
	'heading',
	'layoutColumn',
	'layoutSection',
	'listItem',
	'multiBodiedExtension',
	'nestedExpand',
	'orderedList',
	'panel',
	'paragraph',
	'table',
	'tableCell',
	'tableHeader',
	'tableRow',
	'taskItem',
	'taskList',
]);

const hasRemixableNode = (node: JSONNode): boolean => {
	if (node.type === 'text') {
		return typeof node.text === 'string' && node.text.replace(/\u200B/g, '').trim().length > 0;
	}

	if (node.type === 'hardBreak') {
		return false;
	}

	if (node.content?.some((child) => child !== undefined && hasRemixableNode(child))) {
		return true;
	}

	return !STRUCTURAL_NODE_TYPES.has(node.type);
};

export const containsNativeEmbed = (node: JSONNode | undefined): boolean => {
	if (!node) {
		return false;
	}

	const attrs = node.attrs as Record<string, unknown> | undefined;
	if (
		node.type === 'extension' &&
		attrs?.extensionType === NATIVE_EMBED_EXTENSION_TYPE &&
		typeof attrs.extensionKey === 'string' &&
		attrs.extensionKey.includes(NATIVE_EMBED_EXTENSION_KEY)
	) {
		return true;
	}

	return node.content?.some(containsNativeEmbed) ?? false;
};

export const hasPreparedRemixableContent = (fragment: JSONNode[] | null): boolean =>
	fragment?.some(hasRemixableNode) ?? false;

export const hasRemixableContent = (
	fragment: JSONNode[],
	isNodeUnsupported?: IsNodeUnsupportedForRemix,
): boolean => {
	const preparedFragment = prepareRemixFragmentNodes(fragment, isNodeUnsupported);
	return hasPreparedRemixableContent(preparedFragment);
};
